import { useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, Search, X, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtDate } from '../components/Sales/format';
import { c, btn, card } from '../theme';

// Admin-maintained job_profile -> Job Requisition Category mapping. Jojo
// (Recruiting) owns the real source of truth in Workday -- four Calculated
// Fields, one per category -- and this replaces the "she emails a change,
// someone edits code" loop. Once a job profile is mapped here, it always
// wins over whatever category value a Roster row's own Workday export
// happens to carry. A file upload always shows its diff for confirmation
// before anything is written; the table below is for a single one-off edit.
export default function StaffingJobReqCategories() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['staffing-job-profile-categories'],
    queryFn:  () => api.get('/staffing/job-profile-categories'),
  });

  const categories = data?.categories || ['Admin', 'Operations', 'Mechanic', 'Driver'];
  const mappings   = data?.mappings || [];
  const unmapped   = data?.unmapped || [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['staffing-job-profile-categories'] });

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Mappings"
        title="Job Req Categories"
        subtitle="How a job profile maps to a Job Requisition Category (Admin / Operations / Mechanic / Driver). Upload Jojo's Workday Calculated Field export whenever she changes one -- you'll see exactly what was added, removed, or moved before anything is committed -- or hand-edit a single title below for a one-off change."
      />
      {isLoading ? <LoadingSpinner /> : error ? <div style={s.error}>{error.message}</div> : (
        <>
          {unmapped.length > 0 && (
            <UnmappedPanel unmapped={unmapped} categories={categories} onChanged={invalidate} />
          )}
          <div style={s.grid}>
            {categories.map((cat) => (
              <CategoryCard
                key={cat}
                category={cat}
                mappings={mappings.filter((m) => m.category === cat)}
                onChanged={invalidate}
              />
            ))}
          </div>
          <MappingTable mappings={mappings} categories={categories} onChanged={invalidate} />
        </>
      )}
    </div>
  );
}

// Job profiles on the current roster with no entry in the table at all --
// not caught by MappingTable below, since that only lists what's already
// mapped. See staffingCategoryReference.js's unmappedOnCurrentRoster().
function UnmappedPanel({ unmapped, categories, onChanged }) {
  const [defaultCategory, setDefaultCategory] = useState(categories[0] || '');
  const totalRows = unmapped.reduce((a, u) => a + u.count, 0);

  const setCategory = useMutation({
    mutationFn: ({ jobProfile, category }) =>
      api.patch(`/staffing/job-profile-categories/${encodeURIComponent(jobProfile)}`, { category }),
    onSuccess: (result, { jobProfile }) => {
      toast.success(`${jobProfile} → ${result.new_category}`);
      onChanged();
    },
    onError: (err) => toast.error(err.message || 'Could not save that change.'),
  });

  return (
    <section style={s.warnBlock}>
      <div style={s.warnHead}>
        {unmapped.length} job profile{unmapped.length === 1 ? '' : 's'} on the current roster
        {' '}({totalRows} employee{totalRows === 1 ? '' : 's'}) {unmapped.length === 1 ? 'has' : 'have'} no category mapping at all
      </div>
      <div style={s.warnSub}>
        These show up blank in Job Req Category on the Roster table until mapped here. Pick a category and click
        the title to map it -- each one uses the category picked below unless changed first.
      </div>
      <div style={s.warnDefaultRow}>
        <span style={s.warnLabel}>Map to:</span>
        <select value={defaultCategory} onChange={(e) => setDefaultCategory(e.target.value)} style={s.select}>
          {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
        </select>
      </div>
      <div style={s.unmappedList}>
        {unmapped.map((u) => (
          <div key={u.job_profile} style={s.unmappedRow}>
            <span style={s.unmappedTitle} title={u.job_profile}>{u.job_profile}</span>
            <span style={s.unmappedCount}>{u.count}</span>
            <button
              type="button" style={s.secondaryBtnSm}
              disabled={setCategory.isPending}
              onClick={() => setCategory.mutate({ jobProfile: u.job_profile, category: defaultCategory })}
            >
              + {defaultCategory}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function CategoryCard({ category, mappings, onChanged }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [diff, setDiff] = useState(null); // preview result awaiting confirmation

  const lastUpdated = mappings.reduce((latest, m) => (
    !latest || new Date(m.updated_at) > new Date(latest.updated_at) ? m : latest
  ), null);

  const preview = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      return api.upload(`/staffing/job-profile-categories/preview`, fd);
    },
    onSuccess: (result) => {
      if (!result.added.length && !result.removed.length && !result.moved.length) {
        toast.success(`${category}: no changes -- this file matches what's already stored.`);
        reset();
      } else {
        setDiff(result);
      }
    },
    onError: (err) => toast.error(err.message || 'Could not read that file.'),
  });

  const commit = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      return api.upload(`/staffing/job-profile-categories/upload`, fd);
    },
    onSuccess: (result) => {
      toast.success(
        `${category}: +${result.added.length} / -${result.removed.length}`
        + (result.moved.length ? ` / ${result.moved.length} moved` : '')
        + (result.reclassified_roster_rows ? ` -- ${result.reclassified_roster_rows} roster row(s) updated.` : '.')
      );
      reset();
      onChanged();
    },
    onError: (err) => toast.error(err.message || 'Upload failed.'),
  });

  function reset() {
    setFile(null);
    setDiff(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <section style={s.card}>
      <div style={s.title}>{category}</div>
      <div style={s.count}>{mappings.length} job profile{mappings.length === 1 ? '' : 's'} mapped</div>
      {lastUpdated && (
        <div style={s.meta}>
          Last change {fmtDate(lastUpdated.updated_at)}
          {lastUpdated.updated_by_name ? ` · ${lastUpdated.updated_by_name}` : ''}
        </div>
      )}

      {diff ? (
        <DiffPanel
          diff={diff}
          busy={commit.isPending}
          onConfirm={() => commit.mutate()}
          onCancel={reset}
        />
      ) : (
        <>
          <input
            ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
            onChange={(e) => setFile(e.target.files[0] || null)}
          />
          <div style={s.actions}>
            <button type="button" style={s.secondaryBtn} onClick={() => inputRef.current?.click()} disabled={preview.isPending}>
              <UploadCloud size={14} /> {file ? file.name : 'Choose CF export'}
            </button>
            <button
              type="button"
              style={{ ...s.primaryBtn, opacity: file && !preview.isPending ? 1 : 0.5 }}
              disabled={!file || preview.isPending}
              onClick={() => preview.mutate()}
            >
              {preview.isPending ? 'Reading…' : 'Preview changes'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function DiffPanel({ diff, busy, onConfirm, onCancel }) {
  return (
    <div style={s.diff}>
      {diff.added.length > 0 && (
        <DiffGroup label={`+ ${diff.added.length} added`} items={diff.added} color={c.success} />
      )}
      {diff.removed.length > 0 && (
        <DiffGroup label={`- ${diff.removed.length} removed`} items={diff.removed} color={c.dangerText} />
      )}
      {diff.moved.length > 0 && (
        <DiffGroup
          label={`${diff.moved.length} moved in from another category`}
          items={diff.moved.map((m) => `${m.job_profile} (was ${m.from})`)}
          color={c.warningText}
        />
      )}
      <div style={s.diffActions}>
        <button type="button" style={s.secondaryBtn} onClick={onCancel} disabled={busy}><X size={14} /> Cancel</button>
        <button type="button" style={s.primaryBtn} onClick={onConfirm} disabled={busy}>
          <Check size={14} /> {busy ? 'Committing…' : 'Confirm'}
        </button>
      </div>
    </div>
  );
}

function DiffGroup({ label, items, color }) {
  return (
    <div style={s.diffGroup}>
      <div style={{ ...s.diffLabel, color }}>{label}</div>
      <div style={s.diffItems}>
        {items.slice(0, 12).map((item) => <div key={item} style={s.diffItem}>{item}</div>)}
        {items.length > 12 && <div style={s.diffItem}>…and {items.length - 12} more</div>}
      </div>
    </div>
  );
}

function MappingTable({ mappings, categories, onChanged }) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState(categories[0] || '');

  const setCategory = useMutation({
    mutationFn: ({ jobProfile, category }) =>
      api.patch(`/staffing/job-profile-categories/${encodeURIComponent(jobProfile)}`, { category }),
    onSuccess: (result, { jobProfile }) => {
      toast.success(result.new_category ? `${jobProfile} → ${result.new_category}` : `${jobProfile} removed.`);
      onChanged();
    },
    onError: (err) => toast.error(err.message || 'Could not save that change.'),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = mappings
      .filter((m) => !categoryFilter || m.category === categoryFilter)
      .filter((m) => !q || m.job_profile.toLowerCase().includes(q));
    return [...rows].sort((a, b) => a.job_profile.localeCompare(b.job_profile));
  }, [mappings, search, categoryFilter]);

  return (
    <section style={{ ...card.sm, marginTop: '18px' }}>
      <div style={s.tableHead}>
        <div style={s.searchBox}>
          <Search size={14} color={c.textFaint} />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job profiles…" style={s.searchInput}
          />
        </div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={s.select}>
          <option value="">All categories</option>
          {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        <div style={s.addRow}>
          <input
            value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Add a job profile…" style={s.addInput}
          />
          <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} style={s.select}>
            {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <button
            type="button" style={s.secondaryBtnSm}
            disabled={!newTitle.trim() || setCategory.isPending}
            onClick={() => {
              setCategory.mutate({ jobProfile: newTitle.trim(), category: newCategory });
              setNewTitle('');
            }}
          >
            Add
          </button>
        </div>
      </div>

      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Job Profile</th>
            <th style={s.th}>Category</th>
            <th style={s.th}>Source</th>
            <th style={s.th}>Updated</th>
            <th style={s.th}></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((m) => (
            <tr key={m.job_profile}>
              <td style={s.td}>{m.job_profile}</td>
              <td style={s.td}>
                <select
                  value={m.category}
                  onChange={(e) => setCategory.mutate({ jobProfile: m.job_profile, category: e.target.value })}
                  style={s.select}
                  disabled={setCategory.isPending}
                >
                  {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </td>
              <td style={s.td}>{m.source}</td>
              <td style={s.td}>
                {fmtDate(m.updated_at)}{m.updated_by_name ? ` · ${m.updated_by_name}` : ''}
              </td>
              <td style={s.td}>
                <button
                  type="button" style={s.removeBtn} title="Remove this mapping"
                  disabled={setCategory.isPending}
                  onClick={() => setCategory.mutate({ jobProfile: m.job_profile, category: null })}
                >
                  <X size={13} />
                </button>
              </td>
            </tr>
          ))}
          {!filtered.length && (
            <tr><td style={s.td} colSpan={5}>No job profiles match "{search}".</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

const s = {
  page:  { maxWidth: '1200px', margin: '0 auto' },
  warnBlock: { background: c.warningTint, border: '1px solid #FDE68A', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' },
  warnHead: { fontSize: '13px', fontWeight: 700, color: c.warningText, marginBottom: '4px' },
  warnSub: { fontSize: '11.5px', color: c.warningText, lineHeight: 1.5, marginBottom: '10px' },
  warnDefaultRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' },
  warnLabel: { fontSize: '11.5px', fontWeight: 600, color: c.warningText },
  unmappedList: { display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '320px', overflowY: 'auto' },
  unmappedRow: { display: 'flex', alignItems: 'center', gap: '8px', background: c.surface, borderRadius: '6px', padding: '5px 10px' },
  unmappedTitle: { flex: 1, fontSize: '12px', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  unmappedCount: { fontSize: '11px', color: c.textFaint, minWidth: '24px', textAlign: 'right' },
  grid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' },
  card:  { ...card.sm, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '7px' },
  title: { fontSize: '13px', fontWeight: 600, color: c.text },
  count: { fontSize: '11.5px', color: c.textMuted },
  meta:  { fontSize: '10.5px', color: c.textFaint },
  actions: { display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' },
  primaryBtn:   { ...btn.primarySm },
  secondaryBtn: { ...btn.secondarySm, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' },
  secondaryBtnSm: { ...btn.secondarySm, padding: '4px 10px' },
  error: { color: c.dangerText, fontSize: '12.5px' },

  diff: { ...card.sunk, padding: '10px 12px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '8px' },
  diffGroup: { display: 'flex', flexDirection: 'column', gap: '3px' },
  diffLabel: { fontSize: '11px', fontWeight: 600 },
  diffItems: { fontSize: '10.5px', color: c.textMuted, display: 'flex', flexDirection: 'column', gap: '1px' },
  diffItem:  { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  diffActions: { display: 'flex', gap: '8px', marginTop: '2px' },

  tableHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', padding: '12px 14px' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '6px', background: c.canvas, borderRadius: '8px', padding: '6px 10px', minWidth: '220px' },
  searchInput: { border: 'none', outline: 'none', background: 'transparent', fontSize: '12.5px', color: c.text, flex: 1 },
  addRow: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  addInput: { border: `1px solid ${c.border}`, borderRadius: '6px', padding: '5px 8px', fontSize: '12.5px', color: c.text, minWidth: '200px' },
  select: { border: `1px solid ${c.border}`, borderRadius: '6px', padding: '4px 6px', fontSize: '12px', color: c.text, background: c.surface },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '0.03em', color: c.textFaint, padding: '8px 14px', borderTop: `1px solid ${c.border}` },
  td: { fontSize: '12.5px', color: c.text, padding: '7px 14px', borderTop: `1px solid ${c.border}` },
  removeBtn: { border: 'none', background: 'transparent', color: c.textFaint, cursor: 'pointer', display: 'flex', alignItems: 'center' },
};
